import React, { useState, useEffect, useCallback, useMemo, useRef } from 'react';
import axios from 'axios';
import {
  Box,
  Typography,
  Paper,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  Alert,
  Grid,
  Autocomplete,
  Pagination,
} from '@mui/material';
import { Add as AddIcon } from '@mui/icons-material';
import KalotteCoilLabelDialog from '../components/KalotteCoilLabelDialog';
import type { Dayjs } from 'dayjs';
import {
  CHARGE_FILTERS_KEY,
  formatStoredDay,
  readStoredFilters,
  storedDay,
  storedId,
  storedNumber,
  storedText,
  writeStoredFilters,
} from '../utils/filterStorage';
import {
  ChargeCreator,
  ChargeListItem,
  ChargeUpdate,
  CertificateOption,
  MaterialLexware,
  Lieferant,
} from '../types';
import { apiService } from '../services/api';
import KalotteCertDialog from '../components/KalotteCertDialog';
import ZeugnisHistoryDialog from '../components/ZeugnisHistoryDialog';
import LoadingSpinner from '../components/LoadingSpinner';
import PageHeader from '../components/PageHeader';
import FilterBar from '../components/FilterBar';
import FilterDatePicker from '../components/FilterDatePicker';
import ChargeExcelDialog from '../components/ChargeExcelDialog';
import ChargeResultTable, { ChargeSortColumn } from '../components/ChargeResultTable';
import { useTableSort } from '../utils/tableSort';
import { useAuth } from '../services/AuthContext';
import { useDebounce } from '../hooks/useDebounce';
import { useToast } from '../components/ToastProvider';
import { apiErrorMessage } from '../utils/apiError';

const CHARGES_PAGE_SIZE = 500;

const ChargesPage: React.FC = () => {
  const { user, canWrite, isViewer } = useAuth();
  const { sort, order, onSort } = useTableSort<ChargeSortColumn>('created_at', 'desc', ['created_at']);
  const [rows, setRows] = useState<ChargeListItem[]>([]);
  const hasRows = useRef(false);
  const [total, setTotal] = useState(0);
  const [unfilteredTotal, setUnfilteredTotal] = useState(0);
  const [page, setPage] = useState(1);
  const [certificates, setCertificates] = useState<CertificateOption[]>([]);
  const [materialLexwares, setMaterialLexwares] = useState<MaterialLexware[]>([]);
  const [lexwareLoading, setLexwareLoading] = useState(false);
  const [loading, setLoading] = useState(true);
  const [loadedOnce, setLoadedOnce] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const { toastError } = useToast();

  // Dialog states
  const [dialogOpen, setDialogOpen] = useState(false);
  const [excelOpen, setExcelOpen] = useState(false);
  const [dialogMode, setDialogMode] = useState<'create' | 'edit'>('create');
  const [editCharge, setEditCharge] = useState<ChargeListItem | null>(null);

  // Form states
  const [formCertificateId, setFormCertificateId] = useState<number>(0);
  const [formMaterialLexwareId, setFormMaterialLexwareId] = useState<number>(0);
  const [formBNummer, setFormBNummer] = useState('');
  const [formCoilCount, setFormCoilCount] = useState<number | ''>('');
  const [formNotes, setFormNotes] = useState('');
  const [formOverrideSequence, setFormOverrideSequence] = useState<number | ''>('');

  // Preview states
  const [chargeNumberPreview, setChargeNumberPreview] = useState<string>('');
  const [previewLoading, setPreviewLoading] = useState(false);

  // Zeugnis dialog state
  const [zeugnisChargeId, setZeugnisChargeId] = useState<number | null>(null);
  const [zeugnisChargeNumber, setZeugnisChargeNumber] = useState<string>('');
  const [historyChargeId, setHistoryChargeId] = useState<number | null>(null);
  const [historyChargeNumber, setHistoryChargeNumber] = useState('');
  const [historyPrintingBlocked, setHistoryPrintingBlocked] = useState(false);
  const [issuedConfirmOpen, setIssuedConfirmOpen] = useState(false);

  // Label dialog state
  const [labelChargeId, setLabelChargeId] = useState<number | null>(null);
  const [labelChargeNumber, setLabelChargeNumber] = useState<string>('');

  // Search/Filter states. Saved in the browser until Zurücksetzen.
  const [storedFilters] = useState(() => readStoredFilters(CHARGE_FILTERS_KEY));
  const [searchTerm, setSearchTerm] = useState(() => storedText(storedFilters.search));
  const [lieferantId, setLieferantId] = useState<number | null>(() => storedId(storedFilters.lieferantId));
  const [certificateFilter, setCertificateFilter] = useState(() => storedId(storedFilters.certificateId) ?? 0);
  const [bNummerFilter, setBNummerFilter] = useState(() => storedText(storedFilters.bNummer));
  const [materialLexwareFilter, setMaterialLexwareFilter] = useState(
    () => storedId(storedFilters.materialLexwareId) ?? 0
  );
  const [coilDicke, setCoilDicke] = useState<number | null>(() => storedNumber(storedFilters.coilDicke));
  const [coilBreite, setCoilBreite] = useState<number | null>(() => storedNumber(storedFilters.coilBreite));
  const [creatorId, setCreatorId] = useState<number | null>(() => storedId(storedFilters.creatorId));
  const [creators, setCreators] = useState<ChargeCreator[]>([]);
  const [dateFromFilter, setDateFromFilter] = useState<Dayjs | null>(() => storedDay(storedFilters.dateFrom));
  const [dateToFilter, setDateToFilter] = useState<Dayjs | null>(() => storedDay(storedFilters.dateTo));
  const [lieferanten, setLieferanten] = useState<Lieferant[]>([]);
  const [filtersOpen, setFiltersOpen] = useState(
    () =>
      storedText(storedFilters.bNummer) !== '' ||
      storedId(storedFilters.lieferantId) != null ||
      storedId(storedFilters.certificateId) != null ||
      storedId(storedFilters.materialLexwareId) != null ||
      storedNumber(storedFilters.coilDicke) != null ||
      storedId(storedFilters.creatorId) != null ||
      storedDay(storedFilters.dateFrom) != null ||
      storedDay(storedFilters.dateTo) != null
  );
  const [dateResetKey, setDateResetKey] = useState(0);
  const [searchResetKey, setSearchResetKey] = useState(0);

  const debouncedBNummer = useDebounce(bNummerFilter, 300);
  const bNummerParam =
    bNummerFilter.trim() === '' ? undefined : debouncedBNummer.trim() || undefined;
  const coilSizes = useMemo(() => {
    const seen = new Map<string, MaterialLexware>();
    for (const item of materialLexwares) {
      const key = `${item.coil_dicke}|${item.coil_breite}`;
      if (!seen.has(key)) seen.set(key, item);
    }
    return Array.from(seen.values()).sort(
      (a, b) => a.coil_dicke - b.coil_dicke || a.coil_breite - b.coil_breite
    );
  }, [materialLexwares]);
  const dateFromParam =
    dateFromFilter?.isValid() && dateFromFilter.year() >= 1900
      ? dateFromFilter.startOf('day').toISOString()
      : undefined;
  const dateToParam =
    dateToFilter?.isValid() && dateToFilter.year() >= 1900
      ? dateToFilter.endOf('day').toISOString()
      : undefined;

  const loadPage = useCallback(async (signal?: AbortSignal) => {
    if (!hasRows.current) setLoading(true);
    try {
      const data = await apiService.getChargePage(
        {
          q: searchTerm || undefined,
          lieferant_id: lieferantId ?? undefined,
          certificate_id: certificateFilter || undefined,
          b_nummer: bNummerParam,
          material_lexware_id: materialLexwareFilter || undefined,
          creator_id: creatorId ?? undefined,
          coil_dicke: coilDicke != null && coilBreite != null ? coilDicke : undefined,
          coil_breite: coilDicke != null && coilBreite != null ? coilBreite : undefined,
          date_from: dateFromParam,
          date_to: dateToParam,
          limit: CHARGES_PAGE_SIZE,
          offset: (page - 1) * CHARGES_PAGE_SIZE,
          sort,
          order,
        },
        signal
      );
      setRows(data.items);
      hasRows.current = true;
      setTotal(data.total);
      setUnfilteredTotal(data.unfiltered_total);
      setError(null);
    } catch (err) {
      if (axios.isCancel(err)) return;
      if (axios.isAxiosError(err) && err.response?.status === 422 && hasRows.current) {
        setError(apiErrorMessage(err, 'Die Suche ist zu lang.'));
        return;
      }
      setError('Fehler beim Laden der Chargen');
      console.error('Error loading charges:', err);
    } finally {
      if (!signal?.aborted) {
        setLoading(false);
        setLoadedOnce(true);
      }
    }
  }, [
    searchTerm,
    lieferantId,
    certificateFilter,
    bNummerParam,
    materialLexwareFilter,
    creatorId,
    coilDicke,
    coilBreite,
    dateFromParam,
    dateToParam,
    sort,
    order,
    page,
  ]);

  useEffect(() => {
    const controller = new AbortController();
    void loadPage(controller.signal);
    return () => controller.abort();
  }, [loadPage]);

  useEffect(() => {
    const controller = new AbortController();
    apiService
      .getCertificateOptions(controller.signal)
      .then((data) => setCertificates(data.items))
      .catch((err) => {
        if (!axios.isCancel(err)) console.error(err);
      });
    apiService.getLieferanten().then(setLieferanten).catch((err) => console.error(err));
    return () => controller.abort();
  }, []);

  useEffect(() => {
    setPage(1);
  }, [
    searchTerm,
    lieferantId,
    certificateFilter,
    bNummerParam,
    materialLexwareFilter,
    creatorId,
    coilDicke,
    coilBreite,
    dateFromParam,
    dateToParam,
    sort,
    order,
  ]);

  useEffect(() => {
    if (!filtersOpen || creators.length > 0) return;
    const controller = new AbortController();
    apiService
      .getChargeCreators(controller.signal)
      .then(setCreators)
      .catch((err) => {
        if (!axios.isCancel(err)) console.error(err);
      });
    return () => controller.abort();
  }, [filtersOpen, creators.length]);

  useEffect(() => {
    if ((!dialogOpen && !filtersOpen) || materialLexwares.length > 0) return;
    let cancelled = false;
    setLexwareLoading(true);
    apiService
      .getMaterialLexwares()
      .then((items) => {
        if (!cancelled) setMaterialLexwares(items);
      })
      .catch((err) => {
        if (!cancelled) setError(apiErrorMessage(err, 'Material Lexware konnte nicht geladen werden'));
      })
      .finally(() => {
        if (!cancelled) setLexwareLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dialogOpen, filtersOpen, materialLexwares.length]);

  useEffect(() => {
    updateChargeNumberPreview();
  }, [formCertificateId, formMaterialLexwareId, formOverrideSequence, dialogMode]);

  useEffect(() => {
    writeStoredFilters(CHARGE_FILTERS_KEY, {
      search: searchTerm,
      bNummer: bNummerFilter,
      lieferantId,
      certificateId: certificateFilter || null,
      materialLexwareId: materialLexwareFilter || null,
      creatorId,
      coilDicke,
      coilBreite,
      dateFrom: formatStoredDay(dateFromFilter),
      dateTo: formatStoredDay(dateToFilter),
    });
  }, [
    searchTerm,
    bNummerFilter,
    lieferantId,
    certificateFilter,
    materialLexwareFilter,
    creatorId,
    coilDicke,
    coilBreite,
    dateFromFilter,
    dateToFilter,
  ]);

  const clearFilters = () => {
    setSearchTerm('');
    setSearchResetKey((key) => key + 1);
    setLieferantId(null);
    setCertificateFilter(0);
    setBNummerFilter('');
    setMaterialLexwareFilter(0);
    setCoilDicke(null);
    setCoilBreite(null);
    setCreatorId(null);
    setDateFromFilter(null);
    setDateToFilter(null);
    setDateResetKey((key) => key + 1);
  };

  const loadPageRef = useRef(loadPage);
  loadPageRef.current = loadPage;

  const handleOpenDialog = useCallback((mode: 'create' | 'edit', charge?: ChargeListItem) => {
    setDialogMode(mode);
    setEditCharge(charge || null);
    setFormCertificateId(charge?.certificate_id || 0);
    setFormMaterialLexwareId(charge?.material_lexware_id || 0);
    setFormBNummer(charge?.b_nummer || '');
    setFormCoilCount(charge?.coil_count || '');
    setFormNotes(charge?.notes || '');
    setDialogOpen(true);
  }, []);

  const editRow = useCallback((charge: ChargeListItem) => {
    handleOpenDialog('edit', charge);
  }, [handleOpenDialog]);

  const openLabels = useCallback((charge: ChargeListItem) => {
    setLabelChargeId(charge.id);
    setLabelChargeNumber(charge.charge_number);
  }, []);

  const openZeugnis = useCallback((charge: ChargeListItem) => {
    if (charge.certificate_blocked) return;
    setZeugnisChargeId(charge.id);
    setZeugnisChargeNumber(charge.charge_number);
  }, []);

  const openHistory = useCallback((charge: ChargeListItem) => {
    setHistoryChargeId(charge.id);
    setHistoryChargeNumber(charge.charge_number);
    setHistoryPrintingBlocked(!!charge.certificate_blocked);
  }, []);

  const handleCloseDialog = () => {
    setDialogOpen(false);
    setEditCharge(null);
    setFormCertificateId(0);
    setFormMaterialLexwareId(0);
    setFormBNummer('');
    setFormCoilCount('');
    setFormNotes('');
    setFormOverrideSequence('');
    setChargeNumberPreview('');
  };

  const updateChargeNumberPreview = async () => {
    if (formCertificateId && formMaterialLexwareId && dialogMode === 'create') {
      try {
        setPreviewLoading(true);
        const preview = await apiService.previewChargeNumber({
          certificate_id: formCertificateId,
          material_lexware_id: formMaterialLexwareId,
          notes: formNotes || undefined,
          override_sequence: formOverrideSequence || undefined,
        });
        setChargeNumberPreview(preview.charge_number);
      } catch (err) {
        console.error('Error getting charge number preview:', err);
        setChargeNumberPreview('Fehler beim Laden der Vorschau');
      } finally {
        setPreviewLoading(false);
      }
    } else {
      setChargeNumberPreview('');
    }
  };

  const handleSave = async (acknowledgeIssued = false) => {
    if (!formCertificateId || !formMaterialLexwareId) {
      setError(
        'Bitte wählen Sie sowohl ein Lieferanten Zertifikat als auch ein Material Lexware aus'
      );
      return;
    }

    const linkChanged =
      editCharge != null &&
      (formCertificateId !== editCharge.certificate_id ||
        formMaterialLexwareId !== editCharge.material_lexware_id);
    if (linkChanged && editCharge?.zeugnis_issued_at && !acknowledgeIssued) {
      setIssuedConfirmOpen(true);
      return;
    }

    try {
      const chargeData = {
        certificate_id: formCertificateId,
        material_lexware_id: formMaterialLexwareId,
        b_nummer: formBNummer || undefined,
        coil_count: formCoilCount || undefined,
        notes: formNotes || undefined,
        override_sequence: formOverrideSequence || undefined,
      };

      if (dialogMode === 'create') {
        await apiService.createCharge(chargeData);
      } else if (editCharge) {
        const patch: ChargeUpdate = {};
        if (formCertificateId !== editCharge.certificate_id) {
          patch.certificate_id = formCertificateId;
        }
        if (formMaterialLexwareId !== editCharge.material_lexware_id) {
          patch.material_lexware_id = formMaterialLexwareId;
        }
        if (formBNummer !== (editCharge.b_nummer ?? '')) {
          patch.b_nummer = formBNummer;
        }
        if (formCoilCount !== '' && formCoilCount !== (editCharge.coil_count ?? '')) {
          patch.coil_count = Number(formCoilCount);
        }
        if (formNotes !== (editCharge.notes ?? '')) {
          patch.notes = formNotes;
        }
        await apiService.updateCharge(editCharge.id, patch);
      }

      handleCloseDialog();
      void loadPage();
    } catch (err: any) {
      toastError(apiErrorMessage(err, 'Fehler beim Speichern der Charge'));
      console.error('Error saving charge:', err);
    }
  };

  const handleDelete = useCallback(async (charge: ChargeListItem) => {
    if (!window.confirm(`Soll Charge "${charge.charge_number}" wirklich gelöscht werden?`)) {
      return;
    }

    try {
      await apiService.deleteCharge(charge.id);
      void loadPageRef.current();
    } catch (err: any) {
      toastError(apiErrorMessage(err, 'Fehler beim Löschen der Charge'));
      console.error('Error deleting charge:', err);
    }
  }, [toastError]);

  if (!loadedOnce) {
    return <LoadingSpinner message="Chargen werden geladen..." />;
  }

  const lieferantFilter = lieferanten.find((item) => item.id === lieferantId) ?? null;
  const creatorFilter = creators.find((item) => item.id === creatorId) ?? null;
  const coilSizeFilter =
    coilSizes.find((item) => item.coil_dicke === coilDicke && item.coil_breite === coilBreite) ?? null;
  const activeFilterCount = [
    bNummerParam,
    lieferantId,
    certificateFilter,
    materialLexwareFilter,
    coilDicke != null && coilBreite != null,
    creatorId,
    dateFromParam,
    dateToParam,
  ].filter(Boolean).length;

  return (
    <Box>
      <PageHeader
        title="Chargen verwalten"
        count={total}
        total={unfilteredTotal}
        actions={
          <>
            {!isViewer && (
              <Button variant="outlined" onClick={() => setExcelOpen(true)}>
                Excel
              </Button>
            )}
            {canWrite && (
              <Button variant="contained" startIcon={<AddIcon />} onClick={() => handleOpenDialog('create')}>
                Neue Charge
              </Button>
            )}
          </>
        }
      />

      <ChargeExcelDialog
        open={excelOpen}
        onClose={() => setExcelOpen(false)}
        onImported={() => {
          void loadPage();
        }}
        canImport={canWrite}
      />

      {error && (
        <Alert severity="error" sx={{ mb: 2 }}>
          {error}
        </Alert>
      )}

      <FilterBar
        search={searchTerm}
        onSearch={setSearchTerm}
        debounceMs={300}
        searchResetKey={searchResetKey}
        searchPlaceholder="Charge-Nr., Ursprung, Schmelzland, Lieferant..."
        filtersOpen={filtersOpen}
        onToggleFilters={() => setFiltersOpen((open) => !open)}
        activeCount={activeFilterCount}
        onClear={clearFilters}
      >
        <TextField
          size="small"
          fullWidth
          label="B-Nummer"
          value={bNummerFilter}
          onChange={(event) => setBNummerFilter(event.target.value)}
        />
        <Autocomplete
          options={certificates}
          getOptionLabel={(option) =>
            option.blocked ? `${option.certificate_number} · Unvollständig` : option.certificate_number
          }
          value={certificates.find((c) => c.id === certificateFilter) || null}
          onChange={(_, newValue) => setCertificateFilter(newValue?.id || 0)}
          renderInput={(params) => (
            <TextField {...params} label="Lieferanten Zertifikat" size="small" />
          )}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          size="small"
        />
        <Autocomplete
          options={lieferanten}
          getOptionLabel={(option) => `${option.name} (${option.short_name})`}
          value={lieferantFilter}
          onChange={(_, newValue) => setLieferantId(newValue?.id ?? null)}
          renderInput={(params) => <TextField {...params} label="Lieferant" size="small" />}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          size="small"
        />
        <Autocomplete
          options={materialLexwares}
          loading={lexwareLoading}
          getOptionLabel={(option) =>
            `${option.materialnummer} (${option.coil_dicke} × ${option.coil_breite} mm)`
          }
          value={materialLexwares.find((item) => item.id === materialLexwareFilter) || null}
          onChange={(_, newValue) => setMaterialLexwareFilter(newValue?.id || 0)}
          renderInput={(params) => <TextField {...params} label="Material Lexware" size="small" />}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          size="small"
        />
        <Autocomplete
          options={coilSizes}
          loading={lexwareLoading}
          getOptionLabel={(option) => `${option.coil_dicke} × ${option.coil_breite} mm`}
          value={coilSizeFilter}
          onChange={(_, newValue) => {
            setCoilDicke(newValue?.coil_dicke ?? null);
            setCoilBreite(newValue?.coil_breite ?? null);
          }}
          renderInput={(params) => <TextField {...params} label="Coil Größe" size="small" />}
          isOptionEqualToValue={(option, value) =>
            option.coil_dicke === value.coil_dicke && option.coil_breite === value.coil_breite
          }
          size="small"
        />
        <Autocomplete
          options={creators}
          getOptionLabel={(option) => option.full_name}
          value={creatorFilter}
          onChange={(_, newValue) => setCreatorId(newValue?.id ?? null)}
          renderInput={(params) => <TextField {...params} label="Ersteller" size="small" />}
          isOptionEqualToValue={(option, value) => option.id === value.id}
          size="small"
        />
        <FilterDatePicker
          label="Von Datum"
          resetKey={dateResetKey}
          defaultValue={dateFromFilter}
          onCommit={setDateFromFilter}
        />
        <FilterDatePicker
          label="Bis Datum"
          resetKey={dateResetKey + 1}
          defaultValue={dateToFilter}
          onCommit={setDateToFilter}
        />
      </FilterBar>

      <ChargeResultTable
        rows={rows}
        sort={sort}
        order={order}
        onSort={onSort}
        canWrite={canWrite}
        busy={loading}
        onEdit={editRow}
        onDelete={handleDelete}
        onLabels={openLabels}
        onZeugnis={openZeugnis}
        onHistory={openHistory}
      />

      {total > CHARGES_PAGE_SIZE && (
        <Box sx={{ display: 'flex', justifyContent: 'center', mt: 2 }}>
          <Pagination
            count={Math.ceil(total / CHARGES_PAGE_SIZE)}
            page={page}
            onChange={(_, value) => setPage(value)}
            color="primary"
          />
        </Box>
      )}

      {zeugnisChargeId !== null && (
        <KalotteCertDialog
          chargeId={zeugnisChargeId}
          chargeNumber={zeugnisChargeNumber}
          open={zeugnisChargeId !== null}
          onRecorded={() => {
            void loadPage();
          }}
          onClose={() => {
            setZeugnisChargeId(null);
            setZeugnisChargeNumber('');
          }}
        />
      )}

      {historyChargeId !== null && (
        <ZeugnisHistoryDialog
          chargeId={historyChargeId}
          chargeNumber={historyChargeNumber}
          printingBlocked={historyPrintingBlocked}
          open={historyChargeId !== null}
          onClose={() => {
            setHistoryChargeId(null);
            setHistoryChargeNumber('');
            setHistoryPrintingBlocked(false);
          }}
        />
      )}

      {labelChargeId !== null && (
        <KalotteCoilLabelDialog
          chargeId={labelChargeId}
          chargeNumber={labelChargeNumber}
          open={labelChargeId !== null}
          onClose={() => {
            setLabelChargeId(null);
            setLabelChargeNumber('');
          }}
        />
      )}

      {/* Create/Edit Dialog */}
      <Dialog open={dialogOpen} onClose={handleCloseDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          {dialogMode === 'create' ? 'Neue Charge erstellen' : 'Charge bearbeiten'}
        </DialogTitle>
        <DialogContent>
          <Grid container spacing={1.5}>
            {dialogMode === 'create' && chargeNumberPreview && (
              <Grid item xs={12}>
                <Paper sx={{ p: 1.25, bgcolor: 'background.default' }}>
                  <Typography variant="caption" color="text.secondary">
                    Vorschau Charge-Nummer
                  </Typography>
                  <Typography variant="subtitle1" fontWeight={600}>
                    {previewLoading ? 'Lade...' : chargeNumberPreview}
                  </Typography>
                </Paper>
              </Grid>
            )}
            <Grid item xs={12}>
              <Autocomplete
                options={certificates}
                getOptionLabel={(option) => {
                  const label = `${option.certificate_number} (${option.lieferant_name ?? '-'})`;
                  return option.blocked ? `${label} · Unvollständig` : label;
                }}
                value={certificates.find((c) => c.id === formCertificateId) || null}
                onChange={(_, newValue) => setFormCertificateId(newValue?.id || 0)}
                renderInput={(params) => (
                  <TextField {...params} required label="Lieferanten Zertifikat" />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>
            <Grid item xs={12}>
              <Autocomplete
                options={materialLexwares}
                disabled={lexwareLoading || materialLexwares.length === 0}
                getOptionLabel={(option) =>
                  `${option.materialnummer} (${option.coil_dicke} x ${option.coil_breite} mm)`
                }
                value={materialLexwares.find((m) => m.id === formMaterialLexwareId) || null}
                onChange={(_, newValue) => setFormMaterialLexwareId(newValue?.id || 0)}
                renderInput={(params) => (
                  <TextField {...params} required label="Material Lexware" />
                )}
                isOptionEqualToValue={(option, value) => option.id === value.id}
              />
            </Grid>

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="B-Nummer"
                value={formBNummer}
                onChange={(e) => setFormBNummer(e.target.value)}
                helperText="Optional"
              />
            </Grid>

            <Grid item xs={12} md={6}>
              <TextField
                fullWidth
                label="Anzahl Coils"
                type="number"
                value={formCoilCount}
                onChange={(e) => setFormCoilCount(e.target.value ? Number(e.target.value) : '')}
                helperText="Für Etiketten"
                inputProps={{ min: 1, max: 999 }}
              />
            </Grid>

            {user?.role === 'KALOTTE' && dialogMode === 'create' && (
              <Grid item xs={12} md={6}>
                <TextField
                  fullWidth
                  label="Sequenznummer überschreiben (optional)"
                  type="number"
                  value={formOverrideSequence}
                  onChange={(e) =>
                    setFormOverrideSequence(e.target.value ? Number(e.target.value) : '')
                  }
                  helperText="Leer = automatisch"
                  inputProps={{ min: 1 }}
                />
              </Grid>
            )}

            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Notizen"
                value={formNotes}
                onChange={(e) => setFormNotes(e.target.value)}
                multiline
                rows={2}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseDialog}>Abbrechen</Button>
          <Button
            onClick={() => {
              void handleSave();
            }}
            variant="contained"
            disabled={!formCertificateId || !formMaterialLexwareId}
          >
            {dialogMode === 'create' ? 'Erstellen' : 'Speichern'}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog
        open={issuedConfirmOpen}
        onClose={() => setIssuedConfirmOpen(false)}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Zeugnis bereits ausgestellt</DialogTitle>
        <DialogContent>
          <Alert severity="warning">
            Für Charge {editCharge?.charge_number}
            {editCharge?.zeugnis_number ? ` (${editCharge.zeugnis_number})` : ''} liegt bereits ein
            Zeugnis vor. Das ausgestellte Dokument bleibt, wie es ist. Das nächste Erstellen
            schreibt ein neues Zeugnis, wenn sich die bescheinigten Werte geändert haben.
          </Alert>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setIssuedConfirmOpen(false)}>Abbrechen</Button>
          <Button
            variant="contained"
            onClick={() => {
              setIssuedConfirmOpen(false);
              void handleSave(true);
            }}
          >
            Trotzdem speichern
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default ChargesPage;
